import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, Flex, IconButton, Text } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import useCheckLogin from "../../../hook/useCheckLogin";
import LineBind from "./LineBind";
import { useTranslation } from "react-i18next";
import useWebsiteInfo from "../../../hook/useWebsiteInfo";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { IoClose } from "react-icons/io5";
dayjs.extend(utc);
dayjs.extend(timezone);

const GoLineBind = () => {
  const { t } = useTranslation();
  const i18n = (key) => t(`common.${key}`);
  const { isLogin } = useCheckLogin();
  const isMemberInfo = useSelector((state) => state.isMemberInfo);
  const test = isMemberInfo?.test;
  const is_machine = isMemberInfo?.is_machine;
  const open_member_line_bind = isMemberInfo?.open_member_line_bind;
  const { images, is911vipCC } = useWebsiteInfo();
  const { thirdPartyAccount } = isMemberInfo || {};

  // 今日不再顯示的狀態
  const [checked, setChecked] = useState(false);
  const [hideToday, setHideToday] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // 檢查 localStorage 中是否有今日不顯示的記錄
  useEffect(() => {
    const today = dayjs().format("YYYY-MM-DD");
    const hideTodayKey = `goLineBind_hideToday_${today}`;
    const shouldHideToday = localStorage.getItem(hideTodayKey) === "true";
    setHideToday(shouldHideToday);
    setChecked(shouldHideToday); // 如果今天已經設定不顯示，checkbox 也要打勾
  }, []);

  // 處理打勾變化
  const handleChecked = (e) => {
    const isChecked = e.target.checked;
    setChecked(isChecked);

    const today = dayjs().format("YYYY-MM-DD");
    const hideTodayKey = `goLineBind_hideToday_${today}`;

    if (isChecked) {
      // 設定今日不再顯示到 localStorage，但不立即隱藏組件
      localStorage.setItem(hideTodayKey, "true");
    } else {
      // 如果取消勾選，移除 localStorage 記錄
      localStorage.removeItem(hideTodayKey);
    }
  };

  // 關閉按鈕點擊
  const handleClose = () => {
    setIsVisible(false);
  };

  // 判斷是否已綁定 LINE
  // 判斷有無 line 物件且 status=1 即為已綁定
  const already_bind_line = useMemo(() => {
    return !!(thirdPartyAccount?.line && thirdPartyAccount.line.status === 1);
  }, [thirdPartyAccount]);

  // 取 LINE 綁定註冊時間
  const lineCreatedAt = thirdPartyAccount?.line?.created_at;

  // 設定基準時間（+8時區）
  const forceShowDate = dayjs.tz("2025-07-16 12:00:00", "Asia/Taipei");

  // 判斷是否為新註冊
  let isNewLineRegister = false;
  // 如果 thirdPartyAccount 不是物件（如為陣列）或沒有 line 或沒有 created_at，視為新註冊
  if (
    typeof thirdPartyAccount !== "object" ||
    Array.isArray(thirdPartyAccount) ||
    !thirdPartyAccount.line ||
    !thirdPartyAccount.line.created_at
  ) {
    // 註冊時間判斷只看 isMemberInfo.create_time
    if (isMemberInfo?.create_time) {
      const userRegisterTime = dayjs.tz(
        isMemberInfo.create_time,
        "Asia/Taipei"
      );
      isNewLineRegister =
        userRegisterTime.isAfter(forceShowDate) ||
        userRegisterTime.isSame(forceShowDate);
    } else {
      isNewLineRegister = false;
    }
  } else {
    // 有 line 且有 created_at，仍然只看 isMemberInfo.create_time
    if (isMemberInfo?.create_time) {
      const userRegisterTime = dayjs.tz(
        isMemberInfo.create_time,
        "Asia/Taipei"
      );
      isNewLineRegister =
        userRegisterTime.isAfter(forceShowDate) ||
        userRegisterTime.isSame(forceShowDate);
    } else {
      isNewLineRegister = false;
    }
  }

  // 原本的顯示條件: test === 0、is_machine === 0、網域不能是 911vip.cc、且尚未綁定 LINE
  const shouldShowOrigin =
    test === 0 && is_machine === 0 && !is911vipCC && !already_bind_line;

  // 取 bind_count
  // const bindCount = thirdPartyAccount?.line?.bind_count;
  // 判斷 bind_count 是否小於 2（或沒設定）如果是 就可以綁定
  // const canBindByCount = !bindCount || bindCount < 2;

  // 設 7/23 12:00:00（+8時區）為截止時間
  const stopNewRegisterDate = dayjs.tz("2025-07-23 12:00:00", "Asia/Taipei");
  const isAfterStopDate = dayjs()
    .tz("Asia/Taipei")
    .isAfter(stopNewRegisterDate);

  // 最終顯示條件: 沒有綁定 LINE 且符合原本條件，或是新註冊
  // 如果已經過了截止日期，則只顯示原本條件
  const shouldShow = isAfterStopDate
    ? shouldShowOrigin
    : shouldShowOrigin && isNewLineRegister;

  if (!shouldShow || !isVisible || hideToday) return null;

  return (
    <Flex
      position="fixed"
      top={0}
      left={0}
      width="100vw"
      height="100vh"
      zIndex={9999}
      alignItems="center"
      justifyContent="center"
      bg="rgba(0,0,0,0.8)"
    >
      <Flex
        bgImage={`url('${images.titleBG5}')`}
        bgPosition="center"
        bgRepeat="no-repeat"
        bgSize="cover"
        p={8}
        borderRadius="xl"
        boxShadow="lg"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        position="relative"
        width="80%"
        maxWidth="500px"
      >
        {/* 當 open_member_line_bind === 0 時才顯示 關閉按鈕 */}
        {open_member_line_bind === 0 && (
          <IconButton
            onClick={handleClose}
            fontSize="40px"
            position="absolute"
            top={-8}
            right={-8}
            size="lg"
            bg="white"
            color="black"
            _hover={{ bg: "gray.100" }}
            borderRadius="full"
            zIndex={10}
          >
            <IoClose />
          </IconButton>
        )}
        <Text
          color="white"
          fontSize="2xl"
          fontWeight="bold"
          textAlign="center"
          mb={4}
        >
          {i18n("請先綁定LINE後，再進行遊玩!")}
        </Text>
        <LineBind />
        {/* 當 open_member_line_bind === 0 時才顯示打勾選項 */}
        {open_member_line_bind === 0 && (
          <Checkbox
            colorScheme="purple"
            size="lg"
            onChange={handleChecked}
            zIndex={3}
            checked={checked}
            sx={{
              background:
                "linear-gradient(180deg, #fff 0%,#fdd26a 60%,  #b07f20 80%,  #b07f20 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              textShadow: "0px 1px 3px rgba(255, 215, 0, 0.4)",
              fontSize: "20px",
              fontWeight: 700,
              mt: 4,
            }}
          >
            {i18n("noShow")}
          </Checkbox>
        )}
      </Flex>
    </Flex>
  );
};

export default GoLineBind;
